import type { SeriesIssue } from '@panelvault/shared';
import { ArrowLeft, Download, Heart, ListPlus, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { EmptyState } from '../components/EmptyState';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Panel } from '../components/ui/panel';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { IssueFormDialog } from '../features/catalog/IssueFormDialog';
import { QuickAddDialog } from '../features/catalog/QuickAddDialog';
import { SeriesFormDialog } from '../features/catalog/SeriesFormDialog';
import {
  useCompleteSeries,
  useDeleteSeries,
  useNextIssue,
  useSeriesDetail,
  useWishMissing,
} from '../features/catalog/queries';
import { IssueGapGrid, type GapGridIssue } from '../features/series/IssueGapGrid';
import { imageUrl } from '../lib/api';
import { coverGradient } from '../lib/cover';
import { seriesCoverTransition } from '../lib/transitions';

export function SeriesDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const detail = useSeriesDetail(id);
  const nextIssue = useNextIssue();
  const wishMissing = useWishMissing();
  const completeSeries = useCompleteSeries();
  const [completeNote, setCompleteNote] = useState<string | null>(null);

  async function findMissingVolumes() {
    if (!id) return;
    setCompleteNote(null);
    try {
      const outcome = await completeSeries.mutateAsync(id);
      if (outcome.result) {
        setCompleteNote(
          t('series.completeResult', {
            source: t(`sourceName.${outcome.source}`),
            created: outcome.result.issuesCreated,
            updated: outcome.result.issuesUpdated,
          }) + (outcome.message ? ` ${outcome.message}` : ''),
        );
      } else {
        setCompleteNote(outcome.message ?? t('series.completeNothing'));
      }
    } catch (cause) {
      setCompleteNote(cause instanceof Error ? cause.message : t('form.saveFailed'));
    }
  }
  const deleteSeries = useDeleteSeries();

  const [editOpen, setEditOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [issueDialog, setIssueDialog] = useState<{
    issue?: SeriesIssue;
    number?: string;
    markOwned?: boolean;
  } | null>(null);

  if (detail.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-40" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <EmptyState
        title={t('series.notFound')}
        description={t('series.notFoundBody')}
        action={
          <Link to="/series" className="text-sm text-accent underline">
            {t('series.backToList')}
          </Link>
        }
      />
    );
  }

  const { series, issues, missingNumbers, locations, creators } = detail.data;
  const cover = imageUrl(series.displayCoverImageId, 'large');

  // The gap grid shows known issues plus the numbers that are still missing.
  const knownTiles: GapGridIssue[] = issues.map((issue) => ({
    number: issue.number,
    owned: issue.ownedQuantity > 0,
    imageUrl: imageUrl(issue.coverImageId, 'thumb'),
  }));
  const knownNumbers = new Set(issues.map((issue) => issue.number));
  const gapTiles: GapGridIssue[] = [
    ...knownTiles,
    ...missingNumbers
      .filter((number) => !knownNumbers.has(number))
      .map((number) => ({ number, owned: false })),
  ];

  // Every source that contributed data, so its attribution can be shown.
  const contributingSources = new Set<string>([
    series.dataSource,
    ...Object.values(series.fieldProvenance ?? {}).map((entry) => entry.source),
    ...issues.map((issue) => issue.dataSource),
  ]);
  contributingSources.delete('manual');

  const meta = [series.publisherName, series.volume, series.startYear?.toString()]
    .filter(Boolean)
    .join(' · ');

  // "Autor: Rick Remender · Zeichner: Wes Craig"; creators arrive in role order.
  const creditLine = [...new Set(creators.map((credit) => credit.role))]
    .map(
      (role) =>
        `${t(`creatorRole.${role}`)}: ${creators
          .filter((credit) => credit.role === role)
          .map((credit) => credit.name)
          .join(', ')}`,
    )
    .join(' · ');

  async function removeSeries() {
    if (!id) return;
    if (!window.confirm(t('series.deleteConfirm', { title: series.title }))) return;
    await deleteSeries.mutateAsync(id);
    void navigate('/series');
  }

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/series"
        viewTransition
        className="inline-flex items-center gap-2 text-sm text-muted hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t('series.backToList')}
      </Link>

      <Panel className="flex flex-col gap-5 p-5 sm:flex-row">
        <div
          className="aspect-[2/3] w-32 shrink-0 self-start overflow-hidden rounded-lg border bg-surface-raised"
          style={{
            ...seriesCoverTransition(series.id),
            ...(cover ? {} : { backgroundImage: coverGradient(series.title) }),
          }}
        >
          {cover ? <img src={cover} alt="" className="size-full object-cover" /> : null}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <div>
            <h1 className="text-3xl text-foreground">{series.title}</h1>
            {meta ? <p className="mt-1 text-sm text-muted">{meta}</p> : null}
            {creditLine ? <p className="mt-1 text-sm text-foreground">{creditLine}</p> : null}
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge variant={series.status === 'ongoing' ? 'accent' : 'neutral'}>
                {t(`status.${series.status}`)}
              </Badge>
              <Badge variant="outline">{t(`format.${series.format}`)}</Badge>
              {series.language ? <Badge>{series.language}</Badge> : null}
              {series.manualOverrides.length > 0 ? (
                <Badge variant="success" title={series.manualOverrides.join(', ')}>
                  {t('form.manualFields', { count: series.manualOverrides.length })}
                </Badge>
              ) : null}
            </div>
          </div>

          {contributingSources.size > 0 ? (
            <div className="flex flex-col gap-1 text-xs text-muted">
              <p>
                {t('series.dataFrom', {
                  sources: [...contributingSources]
                    .map((source) => t(`sourceName.${source}`))
                    .join(', '),
                })}
              </p>
              {contributingSources.has('gcd') ? <p>{t('attribution.gcd')}</p> : null}
              {contributingSources.has('comicvine') ? <p>{t('attribution.comicvine')}</p> : null}
            </div>
          ) : null}

          <Progress
            value={series.ownedCount}
            max={series.plannedCount}
            label={t('series.progress', { owned: series.ownedCount, total: series.plannedCount })}
          />

          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setQuickAddOpen(true)}>
              <ListPlus className="size-4" />
              {t('quickAdd.title')}
            </Button>
            <Button size="sm" variant="surface" onClick={() => setIssueDialog({})}>
              <Plus className="size-4" />
              {t('issue.create')}
            </Button>
            <Button
              size="sm"
              variant="surface"
              disabled={nextIssue.isPending}
              onClick={() => id && void nextIssue.mutateAsync(id)}
            >
              {t('issue.next')}
            </Button>
            <Button size="sm" variant="surface" onClick={() => setEditOpen(true)}>
              <Pencil className="size-4" />
              {t('form.edit')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void removeSeries()}>
              <Trash2 className="size-4" />
              {t('form.delete')}
            </Button>
          </div>
        </div>
      </Panel>

      <Panel className="flex flex-col gap-4 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl text-foreground">{t('series.gapTitle')}</h2>

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="surface"
              disabled={completeSeries.isPending}
              onClick={() => void findMissingVolumes()}
            >
              <Search className={completeSeries.isPending ? 'size-4 animate-pulse' : 'size-4'} />
              {completeSeries.isPending ? t('series.completing') : t('series.complete')}
            </Button>
            <a
              href={`/api/series/${series.id}/gaps.txt`}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-surface-raised px-3 text-sm text-foreground hover:bg-border"
            >
              <Download className="size-4" />
              {t('series.exportGaps')}
            </a>
            <Button
              size="sm"
              variant="surface"
              disabled={missingNumbers.length === 0 || wishMissing.isPending}
              onClick={() =>
                id && void wishMissing.mutateAsync({ seriesId: id, numbers: missingNumbers })
              }
            >
              <Heart className="size-4" />
              {t('series.wishMissing', { count: missingNumbers.length })}
            </Button>
          </div>
        </div>

        {detail.data.completing && !completeSeries.isPending ? (
          <p
            role="status"
            className="flex items-center gap-2 rounded-lg border bg-surface-raised p-3 text-sm text-foreground"
          >
            <Search className="size-4 animate-pulse text-accent" />
            {t('series.loadingRun')}
          </p>
        ) : null}

        {completeNote ? (
          <p
            role="status"
            className="rounded-lg border bg-surface-raised p-3 text-sm text-foreground"
          >
            {completeNote}
          </p>
        ) : null}

        {gapTiles.length === 0 ? (
          <EmptyState
            title={t('series.noIssuesTitle')}
            description={t('series.noIssuesBody')}
            action={
              <Button size="sm" onClick={() => setQuickAddOpen(true)}>
                <ListPlus className="size-4" />
                {t('quickAdd.title')}
              </Button>
            }
          />
        ) : (
          <IssueGapGrid
            seriesTitle={series.title}
            issues={gapTiles}
            expectedCount={series.plannedCount}
            onSelect={(tile) => {
              const issue = issues.find((candidate) => candidate.number === tile.number);
              setIssueDialog(issue ? { issue } : { number: tile.number });
            }}
            onWish={(tile) => {
              // A volume that is catalogued but not owned opens with its data,
              // ready to go into the collection; only unknown numbers get an
              // empty form.
              const issue = issues.find((candidate) => candidate.number === tile.number);
              setIssueDialog(issue ? { issue, markOwned: true } : { number: tile.number });
            }}
          />
        )}
      </Panel>

      {issues.length > 0 ? (
        <Panel className="overflow-x-auto">
          <table className="w-full min-w-[40rem] text-sm">
            <thead className="border-b text-left text-xs tracking-wide text-muted uppercase">
              <tr>
                <th className="px-3 py-2">{t('issue.number')}</th>
                <th className="px-3 py-2">{t('issue.title')}</th>
                <th className="px-3 py-2">{t('issue.variant')}</th>
                <th className="px-3 py-2">{t('issue.releaseDate')}</th>
                <th className="px-3 py-2">{t('collection.quantity')}</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((issue) => (
                <tr
                  key={issue.id}
                  className="cursor-pointer border-b last:border-0 hover:bg-surface-raised"
                  onClick={() => setIssueDialog({ issue })}
                >
                  <td className="px-3 py-2 font-display text-foreground">{issue.number}</td>
                  <td className="px-3 py-2 text-muted">{issue.title ?? '–'}</td>
                  <td className="px-3 py-2 text-muted">{issue.variantName ?? '–'}</td>
                  <td className="px-3 py-2 text-muted">{issue.releaseDate ?? '–'}</td>
                  <td className="px-3 py-2">
                    {issue.ownedQuantity > 0 ? (
                      <Badge variant="success">{issue.ownedQuantity}×</Badge>
                    ) : (
                      <Badge variant="outline">{t('series.missing')}</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ) : null}

      <SeriesFormDialog open={editOpen} onClose={() => setEditOpen(false)} series={series} />

      {id ? (
        <QuickAddDialog
          open={quickAddOpen}
          onClose={() => setQuickAddOpen(false)}
          seriesId={id}
          knownLocations={locations}
        />
      ) : null}

      {id ? (
        <IssueFormDialog
          open={issueDialog !== null}
          onClose={() => setIssueDialog(null)}
          seriesId={id}
          issue={issueDialog?.issue}
          defaultNumber={issueDialog?.number}
          markOwned={issueDialog?.markOwned}
        />
      ) : null}
    </div>
  );
}
